import React from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCubes,
    faPlug,
    faCodeCompare,
    faGlobe,
    faGear,
    faCircleQuestion,
    faSun,
    faMoon,
} from "@fortawesome/free-solid-svg-icons";
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import ModulesPage from "./pages/ModulesPage";
import ReleaseNotePage from "./pages/ReleaseNotesPage";
import IdMsPage from "./pages/IdMsPage";
import IdMDetail from "./pages/IdMDetail";
import ModuleDetail from "./pages/ModuleDetail";
import ConnectorsPage from "./pages/ConnectorsPage";
import ConnectorDetail from "./pages/ConnectorDetail";
import SearchPage from "./pages/SearchPage";
import DiffsPage from "./pages/DiffsPage";
import i18n from "./i18n";
import idmIcon from "./logo.png";
import {Modal} from "./shared/Modal";
import { Tooltip } from "./shared/Tooltip";
import { useSettings, SettingsProvider } from "./shared/hooks/useSettings";

function Navigation() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = React.useState("");
    const [darkMode, setDarkMode] = React.useState(() => {
        const stored = localStorage.getItem("theme");
        if (stored === "dark") return true;
        if (stored === "light") return false;

        return window.matchMedia("(prefers-color-scheme: dark)").matches;
    });
    const [isHelpOpen, setIsHelpOpen] = React.useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const { settings, update: updateSettings } = useSettings();

    // collapse the hamburger menu after navigating
    React.useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);

    const barRef = React.useRef<HTMLDivElement>(null);
    const probeRef = React.useRef<HTMLDivElement>(null);
    const sensorRef = React.useRef<HTMLDivElement>(null);
    const [isCollapsed, setIsCollapsed] = React.useState(false);

    // collapse the links behind the hamburger whenever the full row would
    // not fit; measured on a hidden single-row copy of the links, so the
    // decision does not depend on pixel breakpoints (font widths differ
    // across platforms)
    React.useLayoutEffect(() => {
        const bar = barRef.current;
        const probe = probeRef.current;
        const sensor = sensorRef.current;
        if (!bar || !probe || !sensor) return;

        const measure = () => {
            const search = bar.querySelector<HTMLElement>(".nav-search-box");
            const controls = bar.querySelector<HTMLElement>(".nav-controls");
            const gap = 10; // flex gap of .nav-bar
            const required = probe.scrollWidth
                + (search?.offsetWidth ?? 0)
                + (controls?.offsetWidth ?? 0)
                + 2 * gap;
            // hysteresis wider than a scrollbar: collapsing changes the page
            // height, which toggles the scrollbar and with it the bar width —
            // without the margin the state would oscillate
            setIsCollapsed(prev => {
                if (required > bar.clientWidth) return true;
                if (required < bar.clientWidth - 30) return false;
                return prev;
            });
        };

        measure();
        if (typeof ResizeObserver === "undefined") return;
        // the observer fires after layout but before paint; flushing the
        // state synchronously means a wrapped bar is never painted. Only
        // the zero-height sensor and the probe are observed — the bar's own
        // size changes with the collapse, and observing it would make the
        // flush trigger a "loop completed with undelivered notifications"
        const observer = new ResizeObserver(() => flushSync(measure));
        observer.observe(sensor);
        observer.observe(probe);
        return () => observer.disconnect();
        // i18n.isInitialized re-runs the measurement once translations load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.isInitialized]);

    React.useEffect(() => {
        const media = window.matchMedia("(prefers-color-scheme: dark)");
        const listener = (e: MediaQueryListEvent) => {
            if (!localStorage.getItem("theme")) {
                setDarkMode(e.matches);
            }
        };
        media.addEventListener("change", listener);
        return () => media.removeEventListener("change", listener);
    }, []);

    React.useEffect(() => {
        document.documentElement.classList.toggle("dark", darkMode);
        localStorage.setItem("theme", darkMode ? "dark" : "light");
    }, [darkMode]);

    const { t } = useTranslation();

    if (!i18n.isInitialized) {
        return null;
    }

    const navLinks = (
        <>
            <Link
                to="/dashboard"
                className={location.pathname === "/dashboard" || location.pathname === "/" ? "nav-button active" : "nav-button"}
            >
                <span className="nav-content">
                    <img src={idmIcon} alt="IdM" className="nav-icon" style={{height:"20px"}} />
                    <span> {t("navigation.idmVersions")}</span>
                </span>
            </Link>
            <Link
                to="/modules"
                className={
                    location.pathname === "/modules"
                        ? "nav-button active"
                        : "nav-button"
                }
            >
                <span className="nav-content">
                    <span className="nav-icon-emoji"><FontAwesomeIcon icon={faCubes} /></span>
                    <span> {t("navigation.moduleVersions")}</span>
                </span>
            </Link>
            <Link
                to="/connectors"
                className={
                    location.pathname === "/connectors"
                        ? "nav-button active"
                        : "nav-button"
                }
            >
                <span className="nav-content">
                    <span className="nav-icon-emoji"><FontAwesomeIcon icon={faPlug} /></span>
                    <span> {t("navigation.connectorVersions")}</span>
                </span>
            </Link>
            <Link
                to="/diffs"
                className={
                    location.pathname === "/diffs"
                        ? "nav-button active"
                        : "nav-button"
                }
            >
                <span className="nav-content">
                    <span className="nav-icon-emoji"><FontAwesomeIcon icon={faCodeCompare} /></span>
                    <span> {t("navigation.diffs")}</span>
                </span>
            </Link>
        </>
    );

    return (
        <div className="navigation">
            <div className={isCollapsed ? "nav-bar nav-bar--collapsed" : "nav-bar"} ref={barRef}>
                <div className="nav-width-sensor" ref={sensorRef} aria-hidden="true" />
                <button
                    className="theme-toggle nav-hamburger"
                    onClick={() => setIsMenuOpen(prev => !prev)}
                    title={t("navigation.menu")}
                    aria-expanded={isMenuOpen}
                >
                    ☰
                </button>
                <div className={isMenuOpen ? "nav-links nav-links--open" : "nav-links"}>
                    {navLinks}
                </div>
                <div className="nav-links-probe-clip" aria-hidden="true">
                    <div className="nav-links nav-links-probe" ref={probeRef}>
                        {navLinks}
                    </div>
                </div>

                <span className="nav-search-box">
                    <input
                        type="search"
                        className="nav-search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && searchQuery.trim()) {
                                navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                            }
                        }}
                        placeholder={t("search.placeholder")}
                    />
                    <Tooltip label={t("search.tooltip")} className="nav-search-info">
                        i
                    </Tooltip>
                </span>

                <div className="nav-controls">
                    <button
                        className="theme-toggle"
                        onClick={() => {
                            const lang = i18n.language === "cs" ? "en" : "cs";
                            i18n.changeLanguage(lang);
                            localStorage.setItem("lang", lang);
                        }}
                        title={t("settings.language")}
                    >
                        <FontAwesomeIcon icon={faGlobe} /> {i18n.language === "cs" ? "CS" : "EN"}
                    </button>
                    <button
                        className="theme-toggle"
                        onClick={() => setIsSettingsOpen(true)}
                        title={t("settings.title")}
                    >
                        <FontAwesomeIcon icon={faGear} />
                    </button>
                    <button
                        className="theme-toggle"
                        onClick={() => setIsHelpOpen(true)}
                        title={t("help.title")}
                    >
                        <FontAwesomeIcon icon={faCircleQuestion} />
                    </button>
                    <button
                        className="theme-toggle"
                        onClick={() => setDarkMode(prev => !prev)}
                        title={t("settings.theme")}
                    >
                        <FontAwesomeIcon icon={darkMode ? faSun : faMoon} />
                    </button>
                </div>
            </div>
            <Modal
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                title={t("settings.title")}
            >
                <label className="settings-option">
                    <input
                        type="checkbox"
                        checked={settings.showTechnicalNames}
                        onChange={e => updateSettings({ showTechnicalNames: e.target.checked })}
                    />
                    {t("settings.showTechnicalNames")}
                </label>
                <label className="settings-option">
                    <input
                        type="checkbox"
                        checked={settings.descriptionsExpanded}
                        onChange={e => updateSettings({ descriptionsExpanded: e.target.checked })}
                    />
                    {t("settings.descriptionsExpanded")}
                </label>
            </Modal>
            <Modal
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
                title={t("help.title")}
            >
                <div>
                    <p>{t("help.description")}</p>

                    <ul>
                        <li>{t("help.item1")}</li>
                        <li>{t("help.item2")}</li>
                        <li>{t("help.item3")}</li>
                    </ul>

                    <p><strong>{t("help.searchTitle")}</strong></p>
                    <p>{t("help.searchDescription")}</p>
                </div>
            </Modal>
        </div>
    );
}

function App() {
    const { t } = useTranslation();

    return (
        <SettingsProvider>
        <Router basename="/versions">
            <div className="App">
                <h1 className="section-title">{t("app.title")}</h1>
                <Navigation />
                <Routes>
                    <Route path="/" element={<IdMsPage />} />
                    <Route path="/dashboard" element={<IdMsPage />} />
                    <Route path="/modules" element={<ModulesPage />} />
                    <Route path="/connectors" element={<ConnectorsPage />} />
                    <Route path="/idmdetail" element={<IdMDetail />} />
                    <Route path="/moduledetail" element={<ModuleDetail />} />
                    <Route path="/connectordetail" element={<ConnectorDetail />} />
                    <Route path="/release-note" element={<ReleaseNotePage />} />
                    <Route path="/search" element={<SearchPage />} />
                    <Route path="/diffs" element={<DiffsPage />} />
                </Routes>
            </div>
            <footer className="app-footer">
                <a href="https://www.idstory.com/" target="_blank" rel="noopener noreferrer">
                    IdStory.com
                </a>
            </footer>
        </Router>
        </SettingsProvider>
    );
}

export default App;
